import { FC, useCallback, useMemo, MouseEvent, useState } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import {
	EventUserRolesEnum,
	FileVariantsEnum,
	isNoSpotsLeft,
	formatEventTimeRange
} from '@sweatpals/shared';
import { useRouter } from 'next/router';
import Image from 'next/legacy/image';
import { EventType } from '@/modules/_core/types/event-types';
import RemoteImage from '@/modules/_core/components/images/Remote-image';
import PriceBadge from '../price-badge/price-badge.component';
import { eventCardStyles } from './event-card.styles';
import { getPublicFileUrl } from '@/modules/_core/utils/file.utils';
import useLink from '@/modules/_core/hooks/useLink';
import ImageWrapper from '@/modules/_core/components/images/Image-wrapper';
import calendarIcon from '@/public/assets/icons/calendar-outline-white.svg';
import skeletonLogo from '@/public/assets/icons/skeleton-logo.svg';
import { SafeNumber } from '@/modules/_core/types/next.types';
import { eventsService } from '@/modules/_core/services';
import { FadeSpinner } from '@/modules/_core/components/loaders';
import { clsx, Showable, useStyles } from '@sweatpals/react-ui-kit';
import { usePrefetchGetPublicEventInstanceByAliasQuery } from '@/modules/_core/hooks/queries';

export interface EventCardProps {
	disableInteractivity?: boolean;
	eventData?: EventType;
	useEventLink?: boolean;
	useHostLink?: boolean;
	showPriceBadge?: boolean;
	showJoinButton?: boolean;
	adaptiveWidth?: boolean;
	showSkeleton?: boolean;
}

const EventCard: FC<EventCardProps> = ({
	eventData,
	disableInteractivity = false,
	useEventLink = !disableInteractivity,
	useHostLink = !disableInteractivity,
	showPriceBadge = !disableInteractivity,
	showJoinButton = !disableInteractivity,
	adaptiveWidth = false,
	showSkeleton = false,
}) => {
	const [isOpeningEventPage, setIsOpeningEventPage] = useState(false);
	const { makeEventLink, makeHostLink } = useLink();
	const router = useRouter();
	const prefetchEventDetails = usePrefetchGetPublicEventInstanceByAliasQuery();

	const {
		author,
		name,
		instance,
		prices,
		tzid,
		background,
		coHost,
		participantsCount = 0,
		attendeesLimit,
		additionalData,
		isPaid,
		isDonationBased,
		minimalDonationAmount,
	} = eventData ?? {};

	const backgroundUrl = useMemo(() => (
		background
			? getPublicFileUrl(background, { variant: FileVariantsEnum.M })
			: ''
	), [background]);
	const isEventSoldOut = useMemo(
		() => attendeesLimit && isNoSpotsLeft(attendeesLimit, participantsCount, true, !!coHost),
		[attendeesLimit, participantsCount, coHost]
	);

	const styles = useStyles(eventCardStyles, adaptiveWidth, backgroundUrl);

	const openEventPage = useCallback(async (e: MouseEvent<HTMLElement>, isCheckout?: boolean): Promise<void> => {
		e.stopPropagation();
		setIsOpeningEventPage(true);

		try {
			const eventAlias = eventData?.alias;
			const shortLocalInstance = eventData?.calculated_is_serial ? eventData?.shortLocalInstance : undefined

			if (eventAlias) {
				prefetchEventDetails(eventAlias, shortLocalInstance, 'event-card.component')
					.catch(err => console.error('Failed prefetching event details', err));
			}

			const eventLink = makeEventLink({
				eventAlias,
				shortLocalInstance,
				isCheckout,
			});
			await router.push(eventLink);
		} finally {
			setIsOpeningEventPage(false);
		}
	}, [eventData?.alias, eventData?.baseId, eventData?.shortLocalInstance, makeEventLink, router]);

	const openHostPage = useCallback(async (e: MouseEvent<HTMLDivElement>): Promise<void> => {
		if (!useHostLink) {
			return;
		}

		e.stopPropagation();
		const hostLink = makeHostLink({ userName: eventData?.author?.userName });
		await router.push(hostLink);
	}, [useHostLink, makeHostLink, eventData?.author?.userName, router]);

	return (
		<>
			<Showable hide={showSkeleton}>
				<Box position="relative" onClick={openEventPage}>
					<Showable show={isOpeningEventPage}>
						<Box sx={styles.loader}>
							<FadeSpinner size={60} color="white" />
						</Box>
					</Showable>
					<Box sx={clsx(styles.root, useEventLink ? styles.cursorPointer : undefined)}>
						<Image src={backgroundUrl}
									 style={{ zIndex: -1, position: 'absolute', width: '100%', height: '100%', backgroundColor: 'black' }}
									 unoptimized
									 layout={'fill'}
									 loading={'lazy'}
									 objectFit={'cover'}
									 objectPosition={'center'}
						/>
						<Showable show={showPriceBadge}>
							<Box sx={clsx(styles.flexRow, styles.badgesRow)}>
								<PriceBadge
									prices={prices}
									isPaid={isPaid}
									isDonationBased={isDonationBased}
									minDonation={minimalDonationAmount}
								/>
							</Box>
						</Showable>

						<Box sx={clsx(styles.flexColumn, styles.infoSection)}>
							<Showable show={!additionalData?.hideEventDate}>
								<Box sx={styles.instanceRow}>
									<ImageWrapper sx={styles.instanceIcon}>
										<Image
											src={calendarIcon}
											alt="calendar"
											width={styles.instanceIcon.width as SafeNumber}
											height={styles.instanceIcon.height as SafeNumber}
										/>
									</ImageWrapper>
									<Typography sx={styles.instanceText} suppressHydrationWarning>
										{instance ? formatEventTimeRange(instance, null, {
											timezone: tzid,
											startDateFormat: 'eee MMM do, h:mm aa'
										}) : ''}
									</Typography>
								</Box>
							</Showable>
							<Typography sx={styles.eventNameText}>{name}</Typography>
							<Box sx={styles.buttonsSection}>
								<Box
									onClick={openHostPage}
									sx={clsx(styles.flexRow, styles.authorRow, useHostLink ? styles.cursorPointer : undefined)}
								>
									<RemoteImage
										src={author?.avatarId}
										alt="avatar"
										width={30}
										height={30}
										variant={FileVariantsEnum.XS}
									/>
									<Typography sx={styles.eventAuthorText}>{author?.fullName}</Typography>
								</Box>
								<Showable show={showJoinButton}>
									<Button
										disabled={isEventSoldOut}
										sx={styles.joinButton}
										onClick={(e) => {
											openEventPage(e, true);
										}}
									>
										<Typography sx={styles.joinButtonText}>Join</Typography>
									</Button>
								</Showable>
							</Box>
						</Box>
					</Box>
				</Box>
			</Showable>

			<Showable show={showSkeleton}>
				<Box sx={styles.relative}>
					{/* infer size by children size */}
					<Skeleton variant="rectangular">
						<Box sx={clsx(styles.root, styles.skeletonRoot)} />
					</Skeleton>
					{/* draw image on absolute position */}
					<ImageWrapper
						sx={clsx(styles.absolute, {
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
						})}
					>
						<Image src={skeletonLogo} alt="logo" loading={'lazy'} />
					</ImageWrapper>
				</Box>
			</Showable>
		</>
	);
};

export default EventCard;
